/**
 * CodalReferenceModal v1.0
 * 
 * Quick lookup modal for Philippine legal codes
 * Search by article number or keyword, with AI explanation integration
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    X,
    Search,
    Book,
    Copy,
    Check,
    Sparkles,
    Scale,
    Shield,
    Building2,
    ChevronRight,
    Receipt,
    Gavel,
    Users,
    Briefcase
} from 'lucide-react';
import { useHaptics } from '../hooks/useHaptics';
import {
    searchCodalProvisions,
    getProvisionsByCode,
    generateProvisionContext,
    CODAL_CODES,
    CodalCode,
    CodalProvision
} from '../services/codalReferenceService';

interface CodalReferenceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAskAI?: (context: string) => void;
}

const CODE_ICONS: Record<CodalCode, React.ReactNode> = {
    CIVIL_CODE: <Book className="w-4 h-4" />,
    REVISED_PENAL_CODE: <Shield className="w-4 h-4" />,
    CORPORATION_CODE: <Building2 className="w-4 h-4" />,
    LABOR_CODE: <Briefcase className="w-4 h-4" />,
    FAMILY_CODE: <Users className="w-4 h-4" />,
    TAX_CODE: <Receipt className="w-4 h-4" />,
    RULES_OF_COURT: <Gavel className="w-4 h-4" />,
    CONSTITUTION: <Scale className="w-4 h-4" />,
    INSURANCE_CODE: <Shield className="w-4 h-4" />,
};

const CODE_COLORS: Record<CodalCode, string> = {
    CIVIL_CODE: 'from-blue-500 to-blue-600',
    REVISED_PENAL_CODE: 'from-red-500 to-red-600',
    CORPORATION_CODE: 'from-purple-500 to-purple-600',
    LABOR_CODE: 'from-green-500 to-green-600',
    FAMILY_CODE: 'from-pink-500 to-pink-600',
    TAX_CODE: 'from-amber-500 to-amber-600',
    RULES_OF_COURT: 'from-indigo-500 to-indigo-600',
    CONSTITUTION: 'from-yellow-500 to-yellow-600',
    INSURANCE_CODE: 'from-teal-500 to-teal-600',
};

export const CodalReferenceModal: React.FC<CodalReferenceModalProps> = ({
    isOpen,
    onClose,
    onAskAI,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCode, setSelectedCode] = useState<CodalCode | null>(null);
    const [selectedProvision, setSelectedProvision] = useState<CodalProvision | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const haptics = useHaptics();

    // Search results
    const searchResults = useMemo(() => {
        if (searchQuery.trim().length < 2) return [];
        return searchCodalProvisions(searchQuery);
    }, [searchQuery]);

    // Code provisions (when code is selected)
    const codeProvisions = useMemo(() => {
        if (!selectedCode) return [];
        return getProvisionsByCode(selectedCode);
    }, [selectedCode]);

    // Handle copy to clipboard
    const handleCopy = useCallback(async (provision: CodalProvision) => {
        haptics.light();
        const text = `${provision.article} - ${provision.title}\n\n"${provision.text}"`;
        await navigator.clipboard.writeText(text);
        setCopiedId(provision.article);
        setTimeout(() => setCopiedId(null), 2000);
    }, [haptics]);

    // Handle AI explanation
    const handleAskAI = useCallback((provision: CodalProvision) => {
        haptics.medium();
        const context = generateProvisionContext(provision);
        onAskAI?.(context);
        onClose();
    }, [haptics, onAskAI, onClose]);

    // Handle provision select
    const handleProvisionSelect = useCallback((provision: CodalProvision) => {
        haptics.selection();
        setSelectedProvision(provision);
    }, [haptics]);

    // Back to categories
    const handleBack = useCallback(() => {
        haptics.light();
        if (selectedProvision) {
            setSelectedProvision(null);
        } else if (selectedCode) {
            setSelectedCode(null);
        }
    }, [haptics, selectedProvision, selectedCode]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl max-h-[80vh] mx-4 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <div className="flex items-center gap-3">
                        {(selectedCode || selectedProvision) && (
                            <button
                                onClick={handleBack}
                                className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5 rotate-180 text-zinc-400" />
                            </button>
                        )}
                        <div>
                            <h2 className="text-lg font-semibold text-zinc-100">
                                {selectedProvision
                                    ? selectedProvision.article
                                    : selectedCode
                                        ? CODAL_CODES[selectedCode].shortName
                                        : 'Codal Reference'
                                }
                            </h2>
                            <p className="text-xs text-zinc-500">
                                {selectedProvision
                                    ? selectedProvision.title
                                    : 'Philippine Legal Codes'
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                    >
                        <X className="w-5 h-5 text-zinc-400" />
                    </button>
                </div>

                {/* Search Bar */}
                {!selectedProvision && (
                    <div className="p-4 border-b border-zinc-800">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by article number or keyword..."
                                className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-blue-500 transition-colors"
                            />
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Selected Provision Detail View */}
                    {selectedProvision && (
                        <div className="space-y-4">
                            {/* Provision Info */}
                            <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                                {selectedProvision.book && (
                                    <p className="text-xs text-zinc-500 mb-1">{selectedProvision.book}</p>
                                )}
                                {selectedProvision.chapter && (
                                    <p className="text-xs text-zinc-500 mb-2">{selectedProvision.chapter}</p>
                                )}
                                <p className="text-zinc-200 leading-relaxed">
                                    {selectedProvision.text}
                                </p>
                                {selectedProvision.notes && (
                                    <p className="mt-3 text-sm text-amber-400/80 italic">
                                        Note: {selectedProvision.notes}
                                    </p>
                                )}
                            </div>

                            {/* Keywords */}
                            <div className="flex flex-wrap gap-2">
                                {selectedProvision.keywords.map((keyword) => (
                                    <span
                                        key={keyword}
                                        className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-md"
                                    >
                                        {keyword}
                                    </span>
                                ))}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleCopy(selectedProvision)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                                >
                                    {copiedId === selectedProvision.article ? (
                                        <>
                                            <Check className="w-4 h-4 text-green-400" />
                                            <span className="text-green-400">Copied!</span>
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-4 h-4 text-zinc-400" />
                                            <span className="text-zinc-300">Copy Text</span>
                                        </>
                                    )}
                                </button>
                                {onAskAI && (
                                    <button
                                        onClick={() => handleAskAI(selectedProvision)}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg transition-all"
                                    >
                                        <Sparkles className="w-4 h-4 text-white" />
                                        <span className="text-white font-medium">Explain with AI</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Search Results */}
                    {!selectedProvision && searchQuery.trim().length >= 2 && (
                        <div className="space-y-2">
                            <p className="text-xs text-zinc-500 mb-3">
                                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                            </p>
                            {searchResults.map((provision) => (
                                <button
                                    key={`${provision.code}-${provision.article}`}
                                    onClick={() => handleProvisionSelect(provision)}
                                    className="w-full p-3 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-xl text-left transition-all group"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${CODE_COLORS[provision.code]}`}>
                                            {CODE_ICONS[provision.code]}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-zinc-200">{provision.article}</span>
                                                <span className="text-xs text-zinc-500">
                                                    {CODAL_CODES[provision.code].shortName}
                                                </span>
                                            </div>
                                            <p className="text-sm text-zinc-400 truncate">
                                                {provision.title}
                                            </p>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                                    </div>
                                </button>
                            ))}
                            {searchResults.length === 0 && (
                                <p className="text-center text-zinc-500 py-8">
                                    No provisions found for "{searchQuery}"
                                </p>
                            )}
                        </div>
                    )}

                    {/* Code Provisions List */}
                    {!selectedProvision && selectedCode && !searchQuery && (
                        <div className="space-y-2">
                            {codeProvisions.map((provision) => (
                                <button
                                    key={provision.article}
                                    onClick={() => handleProvisionSelect(provision)}
                                    className="w-full p-3 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-xl text-left transition-all group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="font-medium text-zinc-200">{provision.article}</span>
                                            <p className="text-sm text-zinc-400">{provision.title}</p>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Code Categories */}
                    {!selectedProvision && !selectedCode && !searchQuery && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {(Object.entries(CODAL_CODES) as [CodalCode, typeof CODAL_CODES[CodalCode]][])
                                .map(([code, info]) => (
                                    <button
                                        key={code}
                                        onClick={() => {
                                            haptics.light();
                                            setSelectedCode(code);
                                        }}
                                        className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-xl text-left transition-all group"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${CODE_COLORS[code]}`}>
                                                {CODE_ICONS[code]}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-zinc-200 group-hover:text-white transition-colors">
                                                    {info.shortName}
                                                </div>
                                                <p className="text-xs text-zinc-500 mt-0.5">
                                                    {info.name}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
                    <p className="text-xs text-zinc-600 text-center">
                        Tap a provision to view details • Use AI to get explanations
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CodalReferenceModal;
